/**
 * Privacy Policy page - Brutalist minimalism
 */

import React from 'react';

export const Privacy: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-24">
      {/* Header */}
      <div className="mb-10 md:mb-16">
        <h1 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4">Privacy Policy</h1>
        <div className="text-text-secondary font-mono text-xs md:text-sm">
          <span className="block">PermitLoop — Spot Labs Inc.</span>
          <span className="block">Effective Date: March 3, 2026</span>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-8 md:space-y-12 prose-brutalist">
        <div className="text-text-secondary leading-relaxed text-sm md:text-base">
          This Privacy Policy ("Policy") describes how Spot Labs Inc. ("Company," "we," "us," or "our") collects, uses, stores, and shares information when you access or use PermitLoop (the "Service") at permitloop.ai.
        </div>

        <section>
          <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 uppercase tracking-tight">1. Information We Collect</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm md:text-base font-bold mb-2 font-mono uppercase">1.1 Account Information</h3>
              <ul className="space-y-2 text-text-secondary text-sm md:text-base">
                <li className="flex items-start gap-3">
                  <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
                  <span>Name and email address</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
                  <span>Password (stored in hashed form)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
                  <span>Company or organization name (if provided)</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm md:text-base font-bold mb-2 font-mono uppercase">1.2 Project Input Data</h3>
              <p className="text-text-secondary leading-relaxed text-sm md:text-base">
                To generate a permitting Matrix, you provide project-specific information including location, project type, capacity, and other technical parameters.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2 font-mono uppercase text-sm">1.3 Payment Information</h3>
              <p className="text-text-secondary leading-relaxed">
                Payment processing is handled entirely by Stripe, Inc. We do not collect or store your full credit card number, CVV, or bank account details.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">2. How We Use Your Information</h2>
          <ul className="space-y-2 text-text-secondary">
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>Providing, operating, and maintaining the Service</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>Generating permitting Matrices based on your project inputs</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>Managing your account, subscription, and billing</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>Developing, training, and refining our AI models</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>Detecting and preventing fraud, abuse, and security incidents</span>
            </li>
          </ul>
        </section>

        <section className="card border-accent-blue/20 bg-accent-blue/5">
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">3. AI Model Training and Data Use</h2>
          <div className="space-y-4">
            <div className="text-accent-blue font-mono text-sm uppercase mb-4">Our Commitment</div>
            <p className="text-text-secondary leading-relaxed font-bold">
              We will never sell your project data or Matrices to third parties. Your data is used solely to operate the Service and to improve PermitLoop's own AI models for the benefit of all users.
            </p>
            <div>
              <h3 className="text-lg font-bold mb-2 font-mono uppercase text-sm">3.1 Use of Data for Model Training</h3>
              <p className="text-text-secondary leading-relaxed">
                By using the Service, you grant Spot Labs Inc. a non-exclusive, royalty-free, worldwide license to use your project input data and generated Matrices to train, evaluate, fine-tune, and improve the AI models that power PermitLoop.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2 font-mono uppercase text-sm">3.4 Opt-Out</h3>
              <p className="text-text-secondary leading-relaxed">
                If you do not wish your data to be used for AI model training, you may opt out by sending a written request to <a href="mailto:legal@permitloop.ai" className="text-white hover:underline">legal@permitloop.ai</a>. Opt-out requests are honored on a prospective basis.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">4. Storage and Security</h2>
          <p className="text-text-secondary leading-relaxed">
            Your generated Matrices are stored on secure servers. We implement industry-standard technical and organizational measures including encryption in transit (TLS), encryption at rest, access controls, and regular security reviews.
          </p>
          <p className="text-text-secondary leading-relaxed mt-4">
            Your Matrices are retained for as long as your account is active. If you delete your account, we will delete your Matrices within ninety (90) days.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">5. Sharing of Information</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-bold mb-2 font-mono uppercase text-sm">5.1 We Do Not Share Your Matrices</h3>
              <p className="text-text-secondary leading-relaxed">
                We do not share, sell, rent, disclose, or otherwise make available your project input data or generated Matrices to any third party, except as described in this Policy.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2 font-mono uppercase text-sm">5.2 Service Providers</h3>
              <p className="text-text-secondary leading-relaxed mb-2">
                We may share limited personal information with trusted third-party service providers:
              </p>
              <ul className="space-y-2 text-text-secondary">
                <li className="flex items-start gap-3">
                  <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
                  <span>Stripe, Inc. — for payment processing</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
                  <span>Cloud infrastructure providers — for hosting and data storage</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
                  <span>Email delivery providers — for transactional communications</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">6. Your Rights and Choices</h2>
          <p className="text-text-secondary leading-relaxed mb-4">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="space-y-2 text-text-secondary">
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span><strong>Access:</strong> request a copy of the personal information we hold about you</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span><strong>Correction:</strong> request correction of inaccurate or incomplete information</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span><strong>Deletion:</strong> request deletion of your personal information</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span><strong>Portability:</strong> request a machine-readable export of your data</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span><strong>Objection:</strong> object to certain uses of your data, including AI model training</span>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">7. Data Retention</h2>
          <ul className="space-y-2 text-text-secondary">
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>Account information: retained for up to two (2) years after account closure</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>Generated Matrices: up to ninety (90) days following account deletion</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>Payment records: seven (7) years as required by financial regulations</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>Usage data: up to twenty-four (24) months</span>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">8. Contact Us</h2>
          <div className="space-y-2 text-text-secondary font-mono text-sm">
            <div className="flex gap-4">
              <span className="text-text-muted min-w-[120px]">Privacy inquiries:</span>
              <a href="mailto:legal@permitloop.ai" className="text-white hover:underline">legal@permitloop.ai</a>
            </div>
            <div className="flex gap-4">
              <span className="text-text-muted min-w-[120px]">Support:</span>
              <a href="mailto:support@permitloop.ai" className="text-white hover:underline">support@permitloop.ai</a>
            </div>
            <div className="flex gap-4">
              <span className="text-text-muted min-w-[120px]">Website:</span>
              <a href="https://permitloop.ai" className="text-white hover:underline">permitloop.ai</a>
            </div>
            <div className="flex gap-4">
              <span className="text-text-muted min-w-[120px]">Address:</span>
              <span>Spot Labs Inc., 50 Prince St, New York, NY 10012</span>
            </div>
          </div>
        </section>

        <div className="text-text-muted font-mono text-xs pt-8 border-t border-border-dim">
          © 2026 Spot Labs Inc. All rights reserved. Last updated: March 3, 2026
        </div>
      </div>
    </div>
  );
};
